/**
 * MobileProfileFab — плавающая аватарка снизу справа (мобила).
 *
 * Pointer Events + setPointerCapture: на iOS PWA touch-события у правого
 * края экрана могут перехватываться system-gesture'ом. Pointer Events с
 * setPointerCapture гарантируют, что события дойдут до нас.
 */
import { useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/prototype/Avatar";
import { useAuth } from "@/auth/AuthContext";

const HIDDEN_KEY = "mng-profile-fab-hidden";
const HIDE_DRAG_THRESHOLD_PX = 50;
const SHOW_DRAG_THRESHOLD_PX = 25;
const TAP_MOVE_THRESHOLD_PX = 6;
const LONG_PRESS_MS = 450;

export function MobileProfileFab() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState<boolean>(() => {
    try { return localStorage.getItem(HIDDEN_KEY) === "1"; } catch { return false; }
  });
  const fabRef = useRef<HTMLButtonElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const startXRef = useRef<number | null>(null);
  const dragXRef = useRef(0);
  const movedRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);

  function clearLongPress() {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
  }

  useEffect(() => {
    try { localStorage.setItem(HIDDEN_KEY, hidden ? "1" : "0"); } catch { /* ignore */ }
  }, [hidden]);

  if (!user) return null;

  function open() {
    if (movedRef.current) return;
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;  // long-press уже открыл WorkspaceSwitcher
    }
    document.dispatchEvent(new CustomEvent("kb-open-self-profile"));
  }

  // ── FAB pointer handlers (drag RIGHT to hide за правый край) ──
  function onFabPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    startXRef.current = e.clientX;
    dragXRef.current = 0;
    movedRef.current = false;
    pointerIdRef.current = e.pointerId;
    longPressFiredRef.current = false;
    clearLongPress();
    longPressTimerRef.current = setTimeout(() => {
      if (!movedRef.current && startXRef.current != null) {
        longPressFiredRef.current = true;
        document.dispatchEvent(new CustomEvent("kb-open-workspace-switcher"));
      }
      longPressTimerRef.current = null;
    }, LONG_PRESS_MS);
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch { /* some browsers don't support */ }
    if (fabRef.current) fabRef.current.style.transition = "none";
  }
  function onFabPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    if (startXRef.current == null) return;
    if (pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > TAP_MOVE_THRESHOLD_PX) {
      movedRef.current = true;
      clearLongPress();
    }
    if (dx > 0) {
      dragXRef.current = dx;
      if (fabRef.current) fabRef.current.style.transform = `translateX(${dx}px)`;
    }
  }
  function onFabPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const dx = dragXRef.current;
    clearLongPress();
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch { /* ignore */ }
    if (fabRef.current) {
      fabRef.current.style.transition = "transform 0.25s ease-out";
      fabRef.current.style.transform = "";
    }
    dragXRef.current = 0;
    startXRef.current = null;
    pointerIdRef.current = null;
    if (dx > HIDE_DRAG_THRESHOLD_PX) {
      setHidden(true);
    } else if (!movedRef.current) {
      open();
    }
    setTimeout(() => { movedRef.current = false; }, 50);
  }

  // ── Handle (right edge, drag LEFT or tap to show) ──
  function onHandlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    startXRef.current = e.clientX;
    dragXRef.current = 0;
    movedRef.current = false;
    pointerIdRef.current = e.pointerId;
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch { /* ignore */ }
    if (handleRef.current) handleRef.current.style.transition = "none";
  }
  function onHandlePointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    if (startXRef.current == null) return;
    if (pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > TAP_MOVE_THRESHOLD_PX) movedRef.current = true;
    if (dx < 0) {
      dragXRef.current = dx;
      if (handleRef.current) handleRef.current.style.transform = `translateX(${dx}px)`;
    }
  }
  function onHandlePointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const dx = dragXRef.current;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch { /* ignore */ }
    if (handleRef.current) {
      handleRef.current.style.transition = "transform 0.2s ease-out";
      handleRef.current.style.transform = "";
    }
    dragXRef.current = 0;
    startXRef.current = null;
    pointerIdRef.current = null;
    if (dx < -SHOW_DRAG_THRESHOLD_PX) {
      setHidden(false);
    } else if (!movedRef.current) {
      setHidden(false);
    }
    setTimeout(() => { movedRef.current = false; }, 50);
  }

  if (hidden) {
    return (
      <button
        ref={handleRef}
        type="button"
        className="mobile-profile-fab-handle"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
        aria-label="Показать кнопку профиля"
      />
    );
  }

  return (
    <>
      <button
        ref={fabRef}
        type="button"
        className="mobile-profile-fab"
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        onPointerCancel={onFabPointerUp}
        aria-label="Профиль"
      >
        <Avatar
          name={user.display_name || "Я"}
          userId={user.id}
          avatarFilename={user.avatar_filename}
          size={44}
          color="#4fae4e"
        />
      </button>
      {/* Отдельный fixed-position элемент (не child FAB'а — иначе FAB
         setPointerCapture перехватывает все touch и наш click никогда
         не доходит). Лежит поверх FAB в нижне-левом углу. */}
      <button
        type="button"
        className="mobile-profile-fab-collapse"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setHidden(true); }}
        aria-label="Свернуть"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </>
  );
}
